import React, {useState} from 'react';
import Icon from './icons/Icon';
import './UnifiedDashboard.css';

/**
 * UnifiedDashboard Component
 * 
 * A unified view that combines the most important information from:
 * - Project Summary (status, URLs, metadata)
 * - Active Services (running containers)
 * - Live Preview (embedded iframe or quick links)
 * - Quick Actions (deploy, view logs, settings)
 * 
 * This provides Lovable-style all-in-one dashboard experience.
 */
const UnifiedDashboard = ({
  selectedProject,
  selectedEnvironment,
  previewTabs = [],
  activePreviewTab = 0,
  configuration,
  onAction
}) => {
  const [previewMode, setPreviewMode] = useState('embedded'); // 'embedded' | 'links'
  const [previewUrl, setPreviewUrl] = useState('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  const activeService = previewTabs[activePreviewTab];
  const runningServices = previewTabs.filter(tab => tab.url);
  const environments = selectedProject?.environments || [];
  const prodEnv = environments.find(env => env.isProduction);
  const devEnv = environments.find(env => !env.isProduction);

  // Update preview URL when active service changes
  React.useEffect(() => {
    if (activeService?.url && previewUrl !== activeService.url) {
      setPreviewUrl(activeService.url);
    }
  }, [activeService?.url, previewUrl]);

  const handleRefresh = () => {
    setIframeKey(prev => prev + 1);
  };

  const handleUrlChange = (e) => {
    setPreviewUrl(e.target.value);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    setIsEditingUrl(false);
    setIframeKey(prev => prev + 1);
  };

  return (
    <div className="unified-dashboard">
      {/* Top Section: Project Overview */}
      <div className="dashboard-section overview-section">
        <div className="section-header">
          <h2>
            <Icon name="package" size={20} />
            {selectedProject?.name || 'No Project Selected'}
          </h2>
          {selectedProject && (
            <div className="quick-actions-header">
              <button
                className="quick-action-btn"
                onClick={() => onAction?.('open-console')}
                title="Open in Console"
                type="button"
              >
                <Icon name="link" size={14} />
                Console
              </button>
              <button
                className="quick-action-btn"
                onClick={() => onAction?.('settings')}
                title="Settings"
                type="button"
              >
                <Icon name="settings" size={14} />
              </button>
            </div>
          )}
        </div>

        {selectedProject ? (
          <div className="overview-grid">
            {/* Production URL */}
            {prodEnv?.domain && (
              <div className="overview-card">
                <div className="card-label">
                  <Icon name="globe" size={16} />
                  Production
                </div>
                <a
                  className="card-link"
                  href={`https://${prodEnv.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {prodEnv.domain}
                  <Icon name="external-link" size={12} />
                </a>
              </div>
            )}

            {/* Development URL */}
            {devEnv?.domain && (
              <div className="overview-card">
                <div className="card-label">
                  <Icon name="code" size={16} />
                  Development
                </div>
                <a
                  className="card-link"
                  href={`https://${devEnv.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {devEnv.domain}
                  <Icon name="external-link" size={12} />
                </a>
              </div>
            )}

            {/* Resource Count */}
            <div className="overview-card">
              <div className="card-label">
                <Icon name="layers" size={16} />
                Resources
              </div>
              <div className="card-value">
                {configuration ? (
                  <>
                    {Object.keys(configuration.services || {}).length} Services •{' '}
                    {Object.keys(configuration.endpoints || {}).length} APIs •{' '}
                    {Object.keys(configuration.collections || {}).length} Collections
                  </>
                ) : (
                  'Loading...'
                )}
              </div>
            </div>

            {/* Tenant Info */}
            <div className="overview-card">
              <div className="card-label">
                <Icon name="organization" size={16} />
                Organization
              </div>
              <div className="card-value">
                {selectedProject.tenantId || 'N/A'}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-overview">
            <Icon name="package" size={48} />
            <p>Select a project to get started</p>
          </div>
        )}
      </div>

      {/* Middle Section: Live Services & Preview */}
      <div className="dashboard-section services-preview-section">
        <div className="services-column">
          <div className="section-header">
            <h3>
              <Icon name="server" size={18} />
              Live Services
            </h3>
          </div>

          {runningServices.length > 0 ? (
            <div className="services-list">
              {runningServices.map((service, index) => (
                <div key={service.serviceName} className="service-item">
                  <div className="service-status">
                    <span className="status-dot running" />
                  </div>
                  <div className="service-info">
                    <div className="service-name">{service.serviceName}</div>
                    <div className="service-port">
                      <Icon name="link" size={12} />
                      localhost:{service.containerPort}
                    </div>
                  </div>
                  <div className="service-actions">
                    <button
                      className="service-action-btn"
                      onClick={() => onAction?.('view-preview', index)}
                      title="View"
                      type="button"
                    >
                      <Icon name="eye" size={14} />
                    </button>
                    <button
                      className="service-action-btn"
                      onClick={() => onAction?.('open-tab', service.url)}
                      title="Open in new tab"
                      type="button"
                    >
                      <Icon name="external-link" size={14} />
                    </button>
                    <button
                      className="service-action-btn"
                      onClick={() => onAction?.('restart', service.sessionId, service.containerPort)}
                      title="Restart"
                      type="button"
                    >
                      <Icon name="refresh" size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-services">
              <Icon name="server" size={32} />
              <p>No services running</p>
              <button
                className="start-service-btn"
                onClick={() => onAction?.('start-service')}
                type="button"
              >
                <Icon name="play" size={14} />
                Start a Service
              </button>
            </div>
          )}
        </div>

        <div className="preview-column">
          <div className="section-header">
            <h3>
              <Icon name="eye" size={18} />
              Live Preview
            </h3>
            <div className="preview-mode-toggle">
              <button
                className={`mode-btn ${previewMode === 'embedded' ? 'active' : ''}`}
                onClick={() => setPreviewMode('embedded')}
                title="Embedded Preview"
                type="button"
              >
                <Icon name="layout" size={14} />
              </button>
              <button
                className={`mode-btn ${previewMode === 'links' ? 'active' : ''}`}
                onClick={() => setPreviewMode('links')}
                title="Quick Links"
                type="button"
              >
                <Icon name="link" size={14} />
              </button>
            </div>
          </div>

          {activeService?.url ? (
            previewMode === 'embedded' ? (
              <div className="preview-embed">
                <div className="preview-navbar">
                  <button
                    className="nav-btn"
                    onClick={handleRefresh}
                    title="Force Refresh"
                    type="button"
                  >
                    <Icon name="refresh-cw" size={16} />
                  </button>
                  {isEditingUrl ? (
                    <form className="url-form" onSubmit={handleUrlSubmit}>
                      <input
                        className="url-input"
                        type="text"
                        value={previewUrl}
                        onChange={handleUrlChange}
                        onBlur={() => setIsEditingUrl(false)}
                        autoFocus
                      />
                      <button type="submit" className="nav-btn" title="Go">
                        <Icon name="arrow-right" size={16} />
                      </button>
                    </form>
                  ) : (
                    <div
                      className="url-display"
                      onClick={() => setIsEditingUrl(true)}
                      title="Click to edit URL"
                    >
                      <Icon name="globe" size={14} />
                      <span className="url-text">{previewUrl}</span>
                    </div>
                  )}
                  <button
                    className="nav-btn"
                    onClick={() => window.open(previewUrl, '_blank')}
                    title="Open in new tab"
                    type="button"
                  >
                    <Icon name="external-link" size={16} />
                  </button>
                </div>
                <iframe
                  key={iframeKey}
                  className="preview-iframe"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  src={previewUrl}
                  title="Live Preview"
                />
              </div>
            ) : (
              <div className="preview-links">
                {runningServices.map((service) => (
                  <a
                    key={service.serviceName}
                    className="preview-link-card"
                    href={service.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="link-icon">🌐</div>
                    <div className="link-info">
                      <div className="link-title">{service.serviceName}</div>
                      <div className="link-url">{service.url}</div>
                    </div>
                    <Icon name="external-link" size={16} />
                  </a>
                ))}
              </div>
            )
          ) : (
            <div className="empty-preview">
              <Icon name="eye-off" size={32} />
              <p>No preview available</p>
              <p className="hint">Start a service to see live preview</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section: Quick Actions */}
      <div className="dashboard-section actions-section">
        <div className="section-header">
          <h3>
            <Icon name="zap" size={18} />
            Quick Actions
          </h3>
        </div>

        <div className="actions-grid">
          <button
            className="action-card"
            onClick={() => onAction?.('deploy')}
            disabled={!selectedProject}
            type="button"
          >
            <div className="action-icon deploy">
              <Icon color="currentColor" glow name="rocket" size={24} />
            </div>
            <div className="action-info">
              <div className="action-title">Deploy</div>
              <div className="action-description">
                {selectedEnvironment ? `to ${selectedEnvironment.label}` : 'Select environment'}
              </div>
            </div>
          </button>

          <button
            className="action-card"
            onClick={() => onAction?.('add-service')}
            disabled={!selectedProject}
            type="button"
          >
            <div className="action-icon add">➕</div>
            <div className="action-info">
              <div className="action-title">Add Service</div>
              <div className="action-description">Create new microservice</div>
            </div>
          </button>

          <button
            className="action-card"
            onClick={() => onAction?.('view-logs')}
            disabled={runningServices.length === 0}
            type="button"
          >
            <div className="action-icon logs">📋</div>
            <div className="action-info">
              <div className="action-title">View Logs</div>
              <div className="action-description">Debug running services</div>
            </div>
          </button>

          <button
            className="action-card"
            onClick={() => onAction?.('open-console')}
            disabled={!selectedProject}
            type="button"
          >
            <div className="action-icon console">🔗</div>
            <div className="action-info">
              <div className="action-title">Open Console</div>
              <div className="action-description">Mia-Platform Console</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboard;
